import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { resolveSuperAdminAuthBase } from './superAdminAuthBase';
import {
  readCachedSuperAdminSession, clearSuperAdminSessionKeys, deriveSessionState,
  createOnceGuard, SESSION_EXPIRED_MESSAGE,
} from './superAdminSessionGuard';

const SuperAdminAuthContext = createContext(null);

// ISOLATED SuperAdmin auth client. SuperAdmin and the Business/CRM plane are separate auth systems and must
// never share request state. The default `axios` instance carries the global Business `Authorization` default
// AND the global installAxiosResilience interceptor, which on a 401 force-refreshes the tenant FIREBASE token
// and REPLAYS the request with that Business bearer — which must never happen to a SuperAdmin auth POST. A
// dedicated axios.create() instance inherits neither that interceptor nor a later Business Authorization
// default, so SuperAdmin login/OTP/logout run on a clean, tenant-free client (auth POSTs are never retried).
const saClient = axios.create();
// Belt-and-suspenders for the unauthenticated calls: strip any inherited Authorization at request time.
const NO_AUTH = { headers: { Authorization: null } };

// Add this export - this is what's missing!
export const useAuth = () => useContext(SuperAdminAuthContext);

export const useSuperAdminAuth = () => {
  const context = useContext(SuperAdminAuthContext);
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within SuperAdminAuthProvider');
  }
  return context;
};

export const SuperAdminAuthProvider = ({ children }) => {
  const [superAdmin, setSuperAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);
  // 'validating' | 'valid' | 'expired' | 'none' — authenticated is TRUE only when 'valid'.
  const [sessionState, setSessionState] = useState('validating');
  // One-shot expiry guard: an expired session must clear SuperAdmin auth exactly once, so a burst of parallel
  // 401s (or a redirect loop) collapses to a single graceful re-auth.
  const expiryGuard = React.useRef(createOnceGuard());

  // Deterministic SuperAdmin auth base — always ends with /api/v1/superadmin (see resolver above).
  const BACKEND_URL = resolveSuperAdminAuthBase();

  // Graceful expiry: clear ONLY SuperAdmin state/keys (never Business/Firebase), once. No token logged.
  const handleSessionExpired = React.useCallback(() => {
    expiryGuard.current.run(() => {
      clearSuperAdminSessionKeys();
      setSessionToken(null);
      setSuperAdmin(null);
      delete saClient.defaults.headers.common['Authorization'];
      setSessionState('expired');
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    // A cached user is PROVISIONAL: paint identity, but do NOT authenticate until the live session validates.
    const cached = readCachedSuperAdminSession();
    if (!cached) { setSessionState('none'); setLoading(false); return; }
    setSessionToken(cached.token);
    setSuperAdmin(cached.user);            // provisional identity only (isAuthenticated stays false)
    setSessionState('validating');
    saClient.defaults.headers.common['Authorization'] = `Bearer ${cached.token}`;
    let cancelled = false;
    (async () => {
      try {
        await saClient.get(`${BACKEND_URL}/auth/verify-session`, {
          headers: { Authorization: `Bearer ${cached.token}` },
        });
        if (cancelled) return;
        expiryGuard.current.reset();
        setSessionState('valid');
      } catch (e) {
        if (cancelled) return;
        // 401/expired (or any auth failure) → graceful re-auth. A transient network error also fails closed
        // to expired rather than granting access on an unvalidated cached session.
        handleSessionExpired();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // A protected 401 anywhere on the SuperAdmin client triggers the SAME one-shot graceful expiry (no storm).
    const interceptorId = saClient.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) handleSessionExpired();
        return Promise.reject(err);
      },
    );
    return () => { cancelled = true; saClient.interceptors.response.eject(interceptorId); };
  }, [BACKEND_URL, handleSessionExpired]);

  const login = async (identifier, password, mfaMethod) => {
    const payload = {
      password,
      mfa_method: mfaMethod,
    };
    if (mfaMethod === 'sms') {
      payload.phone = identifier;
    } else {
      payload.email = identifier;
    }
    // Isolated client + NO Authorization: a SuperAdmin login must never carry a Business bearer, and a 401
    // here must reject to the caller (never a Firebase-refresh replay).
    const response = await saClient.post(`${BACKEND_URL}/auth/login`, payload, NO_AUTH);
    return response.data;
  };

  // Persist a FULL session (issued only after the complete password → OTP → TOTP ceremony). Never store a
  // pre-auth / restricted token here — those are transient login-stage handles, not a control-plane session.
  const finalizeSession = (session_token, user) => {
    const u = user || { role: 'super_admin' };
    localStorage.setItem('superadmin_session', session_token);
    localStorage.setItem('superadmin_user', JSON.stringify(u));
    setSessionToken(session_token);
    setSuperAdmin(u);
    saClient.defaults.headers.common['Authorization'] = `Bearer ${session_token}`;
    // A fresh, fully-ceremonied session is authenticated immediately; re-arm the expiry guard for next time.
    expiryGuard.current.reset();
    setSessionState('valid');
  };

  // Stage 2 — verify the email/SMS OTP. Returns the NEXT stage; does NOT create a control-plane session.
  //   { stage: 'totp_required', preauth_token, user }  → prompt for authenticator TOTP / recovery code
  //   { stage: 'enrolment_required', enrolment_token, user } → legacy account must enrol an authenticator
  const verifyOTP = async (tempToken, otp) => {
    const response = await saClient.post(`${BACKEND_URL}/auth/verify-otp`, {
      temp_token: tempToken,
      otp,
    }, NO_AUTH);
    return response.data;
  };

  // Stage 3 — authenticator TOTP or a single-use recovery code → FULL session.
  const verifyTotp = async (preauthToken, code) => {
    const response = await saClient.post(`${BACKEND_URL}/auth/verify-totp`, {
      preauth_token: preauthToken,
      code,
    }, NO_AUTH);
    finalizeSession(response.data.session_token, response.data.user);
    return { success: true };
  };

  // Legacy enrolment (restricted session) — start returns the otpauth URI (QR) + manual key.
  const enrolStart = async (enrolmentToken) => {
    const response = await saClient.post(`${BACKEND_URL}/auth/mfa/enroll/start`, {}, {
      headers: { Authorization: `Bearer ${enrolmentToken}` },
    });
    return response.data;
  };

  // Legacy enrolment verify → enrol the factor + upgrade to a FULL session; returns one-time recovery codes.
  const enrolVerify = async (enrolmentToken, code) => {
    const response = await saClient.post(`${BACKEND_URL}/auth/mfa/enroll/verify`, { code }, {
      headers: { Authorization: `Bearer ${enrolmentToken}` },
    });
    finalizeSession(response.data.session_token, response.data.user);
    return { recovery_codes: response.data.recovery_codes || [] };
  };

  const resendOTP = async (tempToken) => {
    const response = await saClient.post(`${BACKEND_URL}/auth/resend-otp`, {
      temp_token: tempToken,
    }, NO_AUTH);
    return response.data;
  };

  const logout = async () => {
    try {
      if (sessionToken) {
        await saClient.post(
          `${BACKEND_URL}/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
      }
    } finally {
      clearSuperAdminSessionKeys();
      setSessionToken(null);
      setSuperAdmin(null);
      delete saClient.defaults.headers.common['Authorization'];
      setSessionState('none');
    }
  };

  return (
    <SuperAdminAuthContext.Provider
      value={{
        superAdmin,
        sessionToken,
        loading,
        login,
        verifyOTP,
        verifyTotp,
        enrolStart,
        enrolVerify,
        resendOTP,
        logout,
        // Authenticated ONLY when the live session validated — a provisional cached user is not enough.
        isAuthenticated: sessionState === 'valid',
        sessionState,
        sessionValidating: sessionState === 'validating',
        sessionExpired: sessionState === 'expired',
        sessionExpiredMessage: SESSION_EXPIRED_MESSAGE,
      }}
    >
      {children}
    </SuperAdminAuthContext.Provider>
  );
};